import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { PageLoader, Card, useToast } from "@/components/ui";
import { Field, TextInput, Select } from "@/components/fields";

export function AdminSettings() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery({ queryKey: ["settings"], queryFn: () => api.get<{ settings: any }>("/settings") });
  const [branding, setBranding] = useState<any>({});
  const [features, setFeatures] = useState<any>({});
  const [defaults, setDefaults] = useState<any>({});

  useEffect(() => {
    if (data?.settings) {
      setBranding(data.settings.branding); setFeatures(data.settings.features); setDefaults(data.settings.defaults);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: ({ key, body }: any) => api.put(`/settings/${key}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["settings"] }); toast.push("success", "Saved"); },
  });

  if (isLoading || !data) return <PageLoader />;

  return (
    <>
      <PageHeader title="Application Settings" subtitle="Branding, feature flags and system defaults. Super Admin only." />
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <h3 className="mb-3 text-sm font-semibold">Branding</h3>
          <div className="space-y-3">
            <Field label="Application name"><TextInput value={branding.appName ?? ""} onChange={(e) => setBranding({ ...branding, appName: e.target.value })} /></Field>
            <Field label="Organisation"><TextInput value={branding.organisation ?? ""} onChange={(e) => setBranding({ ...branding, organisation: e.target.value })} /></Field>
            <Field label="Sign-in tagline"><TextInput value={branding.loginTagline ?? ""} onChange={(e) => setBranding({ ...branding, loginTagline: e.target.value })} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Primary colour"><input type="color" value={branding.primaryColor ?? "#1f3d2b"} onChange={(e) => setBranding({ ...branding, primaryColor: e.target.value })} className="h-9 w-full rounded border border-line" /></Field>
              <Field label="Accent colour"><input type="color" value={branding.accentColor ?? "#b8935a"} onChange={(e) => setBranding({ ...branding, accentColor: e.target.value })} className="h-9 w-full rounded border border-line" /></Field>
            </div>
            <button className="btn-primary" onClick={() => save.mutate({ key: "branding", body: branding })}>Save branding</button>
          </div>
        </Card>

        <Card>
          <h3 className="mb-3 text-sm font-semibold">Features</h3>
          <div className="space-y-2.5">
            {[
              ["deliverableApprovals", "Deliverable sign-off workflow"],
              ["changeRequests", "Change request workflow"],
              ["twoFactor", "Allow users to enable two-factor authentication"],
              ["evidenceRequiredForDone", "Require evidence before an activity can be marked complete"],
            ].map(([k, label]) => (
              <label key={k} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!features[k]} onChange={(e) => setFeatures({ ...features, [k]: e.target.checked })} /> {label}
              </label>
            ))}
            <Field label="Weightage scale">
              <Select value={features.weightageScale ?? "fraction"} onChange={(e) => setFeatures({ ...features, weightageScale: e.target.value })}>
                <option value="fraction">Fraction (0.00–1.00)</option>
                <option value="percent">Percent (0–100)</option>
              </Select>
            </Field>
            <button className="btn-primary" onClick={() => save.mutate({ key: "features", body: features })}>Save features</button>
          </div>
        </Card>

        <Card>
          <h3 className="mb-3 text-sm font-semibold">Defaults for new projects</h3>
          <div className="space-y-3">
            <Field label="Gantt timeline (weeks)"><TextInput type="number" value={defaults.ganttWeeks ?? 70} onChange={(e) => setDefaults({ ...defaults, ganttWeeks: Number(e.target.value) })} /></Field>
            <Field label="Currency"><TextInput value={defaults.currency ?? "OMR"} onChange={(e) => setDefaults({ ...defaults, currency: e.target.value })} /></Field>
            <button className="btn-primary" onClick={() => save.mutate({ key: "defaults", body: defaults })}>Save defaults</button>
          </div>
        </Card>

        <Card>
          <h3 className="mb-2 text-sm font-semibold">Maintenance</h3>
          <p className="text-xs text-ink-faint">
            Deadline reminders run automatically (opportunistically on activity) and can also be triggered by a Cloudflare Cron hitting
            <code className="mx-1 rounded bg-surface-2 px-1">POST /api/cron/deadlines</code> with the <code>x-cron-secret</code> header.
          </p>
        </Card>
      </div>
    </>
  );
}
